import { useEffect, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { Menu, X, Lock, Search, User } from "lucide-react";
import { AnkhMark } from "./AnkhMark";
import { NotificationsBell } from "./NotificationsBell";
import { NAV_LINKS, FEATURED_RELEASE_SLUG, RELEASES, STREAMING_LINKS, type StreamingPlatform } from "@/lib/content";

const FEATURED_RELEASE = RELEASES.find((r) => r.slug === FEATURED_RELEASE_SLUG) ?? RELEASES[0];
import { supabase } from "@/integrations/supabase/client";

export function Navigation() {
  const [open, setOpen] = useState(false);
  const [authed, setAuthed] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setAuthed(Boolean(data.user)));
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN") setAuthed(true);
      else if (event === "SIGNED_OUT") setAuthed(false);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  return (
    <header className="sticky top-0 z-50 border-b hairline bg-obsidian/85 backdrop-blur-xl">
      <div className="mx-auto grid max-w-7xl grid-cols-[auto_1fr_auto] items-center gap-4 px-4 py-3 sm:px-6 lg:py-4">
        <Link to="/" className="flex min-w-0 items-center gap-2" aria-label="Buddha KA — home">
          <AnkhMark size={32} animated />
          <span className="font-serif text-lg tracking-wide sm:text-xl">
            <span className="text-gold-luminous">BUDDHA</span> KA
          </span>
        </Link>

        <nav aria-label="Primary" className="hidden min-w-0 justify-center lg:flex">
          <ul className="flex min-w-0 items-center gap-4 overflow-x-auto text-[13px] [scrollbar-width:none] xl:gap-6 xl:text-sm [&::-webkit-scrollbar]:hidden">
            {NAV_LINKS.map((l) => {
              const active = pathname === l.to || (l.to !== "/" && pathname.startsWith(l.to));
              return (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className={`relative whitespace-nowrap py-2 transition-colors ${active ? "text-gold-luminous" : "text-foreground/80 hover:text-gold"}`}
                  >
                    {l.label}
                    {active && (
                      <span className="absolute -bottom-0.5 left-0 right-0 h-px bg-gold-gradient" />
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="flex items-center gap-1 sm:gap-2 justify-self-end">
          <button
            type="button"
            aria-label="Search the ecosystem"
            onClick={() => (window as unknown as { openCommandPalette?: () => void }).openCommandPalette?.()}
            className="group flex h-11 items-center gap-2 rounded-full px-2 text-foreground/80 hover:text-gold sm:pr-3"
          >
            <Search size={18} />
            <kbd className="hidden rounded border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] font-medium tracking-wider text-muted-foreground group-hover:text-gold/80 sm:inline">
              ⌘K
            </kbd>
          </button>
          {authed && (
            <Link
              to="/dashboard"
              className="hidden rounded-full border hairline px-3 py-2 text-sm text-foreground/80 transition-colors hover:border-gold hover:text-gold lg:inline-flex"
            >
              Dashboard
            </Link>
          )}
          {authed && (
            <Link
              to="/library"
              className="hidden rounded-full border hairline px-3 py-2 text-sm text-foreground/80 transition-colors hover:border-gold hover:text-gold lg:inline-flex"
            >
              Library
            </Link>
          )}
          {authed && <NotificationsBell />}
          <Link
            to={authed ? "/account" : "/auth"}
            aria-label={authed ? "Your account" : "Sign in"}
            className="hidden h-11 w-11 items-center justify-center rounded-full text-foreground/80 hover:text-gold sm:flex"
          >
            <User size={18} />
          </Link>
          <Link
            to="/download"
            className="hidden rounded-full border border-gold px-4 py-2 text-sm font-medium text-gold-luminous transition-all hover:bg-gold hover:text-obsidian md:inline-flex"
          >
            Get the App
          </Link>
          <button
            type="button"
            aria-label="Open menu"
            aria-expanded={open}
            onClick={() => setOpen(true)}
            className="flex h-11 w-11 items-center justify-center rounded-full text-foreground hover:text-gold lg:hidden"
          >
            <Menu size={22} />
          </button>
        </div>
      </div>

      {/* Mobile full-screen menu */}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
          className="fixed inset-0 z-[100] flex flex-col bg-obsidian animate-rise"
        >
          <div className="grid grid-cols-[auto_1fr_auto] items-center gap-4 border-b hairline px-4 py-3 sm:px-6">
            <div className="flex items-center gap-2">
              <AnkhMark size={28} />
              <span className="font-serif text-lg">BUDDHA KA</span>
            </div>
            <span />
            <button
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              className="flex h-11 w-11 items-center justify-center rounded-full hover:text-gold"
            >
              <X size={22} />
            </button>
          </div>
          <nav aria-label="Mobile menu" className="flex-1 overflow-y-auto px-6 py-8">
            <ul className="space-y-1">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className="block border-b hairline py-4 font-serif text-3xl text-foreground hover:text-gold-luminous"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-10 space-y-4">
              {authed && (
                <Link
                  to="/dashboard"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between rounded-full border border-gold/50 px-5 py-4 text-sm text-gold-luminous hover:bg-gold hover:text-obsidian"
                >
                  <span>Dashboard</span>
                  <span>→</span>
                </Link>
              )}
              {authed && (
                <Link
                  to="/library"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between rounded-full border hairline px-5 py-4 text-sm hover:border-gold hover:text-gold"
                >
                  <span>Your Library</span>
                  <span className="text-gold">→</span>
                </Link>
              )}
              <Link
                to="/search"
                onClick={() => setOpen(false)}
                className="flex items-center justify-between rounded-full border hairline px-5 py-4 text-sm hover:border-gold hover:text-gold"
              >
                <span className="inline-flex items-center gap-2"><Search size={16} /> Search the ecosystem</span>
                <span className="text-gold">→</span>
              </Link>
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="block rounded-full bg-gold-gradient px-6 py-4 text-center text-sm font-semibold text-obsidian shadow-gold"
              >
                Send a transmission
              </Link>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Featured Release</p>
              <Link
                to="/music/$slug"
                params={{ slug: FEATURED_RELEASE_SLUG }}
                onClick={() => setOpen(false)}
                className="block rounded-lg border hairline bg-charcoal p-4 hover:border-gold"
              >
                <p className="font-serif text-lg text-gold-luminous">{FEATURED_RELEASE.title}</p>
                <p className="mt-1 text-xs text-muted-foreground">Current {FEATURED_RELEASE.type}</p>
              </Link>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground pt-4">Streaming</p>
              <div className="flex flex-wrap gap-2">
                {(["Spotify", "Apple Music", "YouTube", "SoundCloud"] as StreamingPlatform[]).map((p) => {
                  const url = STREAMING_LINKS[p];
                  return url ? (
                    <a
                      key={p}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => setOpen(false)}
                      className="inline-flex items-center gap-1 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs text-gold-luminous hover:bg-gold hover:text-obsidian"
                    >
                      {p} →
                    </a>
                  ) : (
                    <span key={p} className="inline-flex items-center gap-1 rounded-full border hairline px-3 py-1 text-xs text-muted-foreground">
                      <Lock size={10} aria-hidden /> {p}
                    </span>
                  );
                })}
              </div>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
